import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { menuInfo } from "@shared/type/CommonType";
import SearchPanel from "@widgets/SearchPanel";
import { showDialog } from "@widgets/CommonDialog";
import { showResizablePopupModal } from "@widgets/CommonModal";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnButton from "@shared/components/SnButton";
import SN_MAS_SET_REG_007_POP_01 from "./SN_MAS_SET_REG_007_POP_01";

interface Props {
  Menuprops: menuInfo;
}

// 메시지전송설정
const SN_MAS_SET_REG_007 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(), // 알람정보(config)
    grid2: React.createRef(), // 메뉴
    grid3: React.createRef(), // 알람그룹(gp)
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  // 현재 선택 알람
  const selectedAlarm = useRef<any>(null);
  // _01 다중결과셋으로 자식 전체 받아 캐시 → 행 선택 시 cd_alarm 필터
  const allMenu = useRef<any[]>([]);
  const allGp = useRef<any[]>([]);
  // 저장 후 각 그리드 재로딩(자동 0행포커스) 뒤 복원할 선택.
  // 재로딩이 여러 번 일어나므로(특히 grid2/3) 매 onModelUpdated 마다 다시 선택하고,
  // 타이머로 잠시 뒤 정리한다(그 tick 의 0행 자동선택보다 항상 뒤에 실행됨).
  const pendingReselect = useRef<{
    contextAlarm?: string; // 저장 시점의 선택 알람 — 사용자가 다른 알람으로 이동하면 자식 재선택 중단
    alarm?: string;
    menu?: string;
    gp?: { cd_gp_send: string; no_line: any };
  } | null>(null);
  const reselectTimer = useRef<any>(null);

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1 || [],
          grid2: rtn.grid.grid2 || [],
          grid3: rtn.grid.grid3 || [],
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({ onSearch, onInsert, onDelete, onSave }));

  const clearChildren = () => {
    setDataSources((prev: any) => ({ ...prev, grid2: [], grid3: [] }));
    selectedAlarm.current = null;
    allMenu.current = [];
    allGp.current = [];
  };

  // 데이터셋 조회 → 캐시(allMenu/allGp) 갱신. updateGrid1=true 면 grid1 rowData 도 교체.
  // (자식만 바뀐 저장에서는 grid1 을 건드리지 않아야 선택/포커스가 유지된다.)
  const loadDataset = async (updateGrid1 = true): Promise<any[] | null> => {
    const data: any = await fnGetDataSet({
      id: "sn_mas_set_reg_007_01",
      params: searchRef.current.getFormdata(),
    });
    if (!Array.isArray(data)) return null; // 에러 시 fnGetDataSet 가 다이얼로그 표시 후 undefined 반환
    // _01 다중결과셋: [0]알람정보 [1]메뉴 [2]알람그룹 (전 알람분)
    allMenu.current = data[1] || [];
    allGp.current = data[2] || [];
    if (updateGrid1) {
      setDataSources((prev: any) => ({ ...prev, grid1: data[0] || [] }));
      gridRefs.current["grid1"].current?.resetUpdate();
    }
    return data[0] || [];
  };

  // 렌더 후 조건에 맞는 행을 선택(셀 포커스는 grid 설정을 따름 → grid2/3 는 도움창 안 열림).
  const reselectAfterRender = (
    gridKey: string,
    match: (d: any) => boolean,
    attempt = 0
  ) => {
    const api = gridRefs.current[gridKey]?.current;
    if (!api?.forEachNode) return;
    let idx = -1;
    api.forEachNode((n: any) => {
      if (idx < 0 && match(n?.data)) idx = n.rowIndex ?? -1;
    });
    if (idx >= 0) {
      api.selectAndFocusRow?.(idx);
      return;
    }
    if (attempt < 20)
      requestAnimationFrame(() =>
        reselectAfterRender(gridKey, match, attempt + 1)
      );
  };

  const onSearch = async () => {
    const changed = gridRefs.current["grid1"].current?.getChange();
    if (changed && changed.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        if (!(await onSave())) return;
      } else if (confirm === undefined) {
        return;
      }
    }

    try {
      clearChildren();
      await loadDataset();
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  // 신규/삭제는 알람그룹(grid3)만 대상. 알람정보(grid1)·메뉴(grid2)는 회사제공(추가·삭제 불가).
  const onInsert = () => {
    addGpRow();
  };

  const onDelete = async () => {
    const sel = gridRefs.current["grid3"].current?.getSelectedRows?.()?.[0];
    if (!sel) {
      await showDialog("alter", "삭제할 알람그룹을 선택하세요.");
      return;
    }
    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;
    gridRefs.current["grid3"].current?.deleteSelectedRows();
  };

  // 추가사항적용: sys_alarm_route → 회사 config 신규 반영 후 재조회
  const onApplyRoute = async () => {
    const ok = await fnSave("SN_MAS_SET_REG_007_APPLY_01", {
      cd_firm: userInfo.cd_firm,
      cd_user: userInfo.cd_user,
    });
    if (ok) await onSearch();
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();
      gridRefs.current["grid3"].current?.stopEditing();

      const cfg = gridRefs.current["grid1"].current.getChange();
      const menu = gridRefs.current["grid2"].current?.getChange() ?? {};
      const gp = gridRefs.current["grid3"].current?.getChange() ?? {};

      const total =
        (cfg.modifiedRowcount || 0) +
        (menu.modifiedRowcount || 0) +
        (gp.modifiedRowcount || 0);
      if (total === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const saveData = {
        sys_alarm_config: cfg,
        sys_alarm_menu: menu,
        sys_alarm_gp: gp,
      };
      // 재조회 후 복원할 포커스 캡처
      const keepAlarm = selectedAlarm.current?.cd_alarm;
      const keepMenu =
        gridRefs.current["grid2"].current?.getSelectedRows?.()?.[0]?.cd_menu;
      const g3sel = gridRefs.current["grid3"].current?.getSelectedRows?.()?.[0];
      const keepGp = g3sel
        ? { cd_gp_send: g3sel.cd_gp_send, no_line: g3sel.no_line }
        : null;

      const ok = await fnSave("SN_MAS_SET_REG_007_SAVE_01", saveData);
      if (!ok) {
        gridRefs.current["grid1"].current.resetUpdate(true);
        return false;
      }
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current?.resetUpdate();
      gridRefs.current["grid3"].current?.resetUpdate();

      // 저장 후 재조회. config 가 안 바뀌었으면 grid1 을 재로딩하지 않아 선택이 그대로 유지된다.
      // (재로딩하면 SnGrid 가 0번 행을 자동 포커스해 우리의 재선택을 덮어씀)
      const cfgChanged = (cfg.modifiedRowcount || 0) > 0;
      const rows = await loadDataset(cfgChanged);
      if (rows) {
        // 각 그리드는 재로딩 시 SnGrid 가 0행을 자동선택하므로, onModelUpdated 에서 재선택한다.
        // grid2/grid3 는 (config 변경 시 grid1 재선택 → onAlarmFocused, 아니면 아래 직접) 재로딩된다.
        pendingReselect.current = {
          contextAlarm: keepAlarm,
          alarm: cfgChanged ? keepAlarm : undefined,
          menu: keepMenu,
          gp: keepGp ?? undefined,
        };
        // 재로딩이 끝날 때까지(약 1s) 매 onModelUpdated 마다 재선택 → 이후 정리
        if (reselectTimer.current) clearTimeout(reselectTimer.current);
        reselectTimer.current = setTimeout(() => {
          pendingReselect.current = null;
        }, 1000);
        if (!cfgChanged && selectedAlarm.current) {
          // grid1 유지 → 자식만 최신 캐시로 갱신(→ grid2/grid3 onModelUpdated 에서 재선택)
          onAlarmFocused(selectedAlarm.current);
        }
      }
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      return false;
    }
  };

  // 알람 행 선택 → 캐시에서 cd_alarm 필터 (추가 조회 없음)
  const onAlarmFocused = (row: any) => {
    if (!row) return;
    selectedAlarm.current = row;
    const cdAlarm = row.cd_alarm;
    if (!cdAlarm) {
      setDataSources((prev: any) => ({ ...prev, grid2: [], grid3: [] }));
      return;
    }
    const copy = (cache: any[]) =>
      cache.filter((r) => r.cd_alarm === cdAlarm).map((r) => ({ ...r }));
    setDataSources((prev: any) => ({
      ...prev,
      grid2: copy(allMenu.current),
      grid3: copy(allGp.current),
    }));
    gridRefs.current["grid2"].current?.resetUpdate();
    gridRefs.current["grid3"].current?.resetUpdate();
  };

  // 수신자 도움창 → 선택 수신자들을 grid3 에 추가 (발생그룹 cd_gp_send 는 그리드에서 편집)
  const addGpRow = async () => {
    const a = selectedAlarm.current;
    if (!a?.cd_alarm) return showDialog("alter", "알람을 먼저 선택하세요.");
    const result = await showResizablePopupModal({
      title: "수신자 도움창",
      children: <SN_MAS_SET_REG_007_POP_01 />,
      validation: true,
      width: "1200px",
      height: "800px",
    });
    if (!result || result === "close") return;
    const recv: any[] = result[0] ?? [];
    // no_line 은 비표시 PK라 그리드 노드에서 직접 최대값 계산
    let base = 0;
    gridRefs.current["grid3"].current?.forEachNode?.((n: any) => {
      const v = Number(n?.data?.no_line) || 0;
      if (v > base) base = v;
    });
    recv.forEach((r) => {
      base += 1;
      gridRefs.current["grid3"].current?.addRow({
        cd_firm: userInfo.cd_firm,
        cd_alarm: a.cd_alarm,
        cd_gp_send: "",
        no_line: base,
        fg_gp: r.fg_gp,
        nm_fg_gp: r.gubun, // 그룹/유저 (표시용)
        cd_receiver: r.cd_receiver,
        nm_receiver: r.nm_receiver,
      });
    });
  };

  const gridBtn = (add: () => void, del: () => void) => ({
    onBtnAddClick: add,
    onBtnDeleteClick: del,
  });

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1 flex">
        <div className="flex-1 min-w-0">
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
          />
        </div>
        {/* SearchPanel 의 구분선(<hr>)은 패널 폭까지만 그려진다. 버튼이 차지한 만큼 끊겨 보이므로
            같은 선을 버튼 아래까지 이어 그린다(접기화살표 15px 만큼 버튼을 위로 올린다). */}
        <div className="flex flex-col">
          <div className="flex-1 flex items-center pl-2 pb-[15px]">
            <SnButton text={"추가사항적용"} onClick={onApplyRoute} />
          </div>
          <div style={{ height: "1px", background: "rgb(186,191,199)" }} />
        </div>
      </div>
      <div className="w-full flex flex-1 min-h-0 space-x-1.5">
        {/* 좌측: 알람정보 */}
        <SnResizeBar direction={"width"} id="msg_panel1" orgSize="60%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={columns.grid1}
            rowData={dataSources.grid1}
            etcProps={{
              isEditable: true,
              rowSelection: "single",
              title: "알람정보",
            }}
            events={{
              onFocusedRowChanged: onAlarmFocused,
              // 재로딩 후(자동 0행포커스 뒤) 저장 전 선택 알람으로 재선택
              onModelUpdated: () => {
                const alarm = pendingReselect.current?.alarm;
                if (!alarm) return;
                requestAnimationFrame(() =>
                  reselectAfterRender("grid1", (d) => d?.cd_alarm === alarm)
                );
              },
            }}
          />
        </SnResizeBar>

        {/* 우측: 메뉴(상) + 알람그룹(하) */}
        <SnResizeBar direction={"width"} id="msg_panel2" orgSize="50%">
          <div className="h-full flex flex-col min-h-0 space-y-1.5">
            <div className="flex-1 min-h-0">
              <SnGrid
                ref={gridRefs.current["grid2"]}
                columnConfig={columns.grid2}
                rowData={dataSources.grid2}
                etcProps={{
                  isEditable: true,
                  rowSelection: "single",
                  title: "메뉴",
                  suppressInitialCellFocus: true,
                }}
                events={{
                  // 메뉴(grid2)는 회사제공 — 신규/삭제 버튼 없음. 사용여부(yn_use)만 편집.
                  onModelUpdated: () => {
                    const p = pendingReselect.current;
                    if (!p?.menu) return;
                    // 사용자가 다른 알람으로 이동했으면 자식 재선택 중단
                    if (selectedAlarm.current?.cd_alarm !== p.contextAlarm)
                      return;
                    requestAnimationFrame(() =>
                      reselectAfterRender("grid2", (d) => d?.cd_menu === p.menu)
                    );
                  },
                }}
              />
            </div>
            <div className="flex-1 min-h-0">
              <SnGrid
                ref={gridRefs.current["grid3"]}
                columnConfig={columns.grid3}
                rowData={dataSources.grid3}
                etcProps={{
                  isEditable: true,
                  rowSelection: "single",
                  title: "알람그룹",
                  suppressInitialCellFocus: true,
                }}
                events={{
                  ...gridBtn(addGpRow, () =>
                    gridRefs.current["grid3"].current?.deleteSelectedRows()
                  ),
                  onModelUpdated: () => {
                    const p = pendingReselect.current;
                    if (!p?.gp) return;
                    // 사용자가 다른 알람으로 이동했으면 자식 재선택 중단
                    if (selectedAlarm.current?.cd_alarm !== p.contextAlarm)
                      return;
                    const gp = p.gp;
                    requestAnimationFrame(() =>
                      reselectAfterRender(
                        "grid3",
                        (d) =>
                          d?.cd_gp_send === gp.cd_gp_send &&
                          String(d?.no_line) === String(gp.no_line)
                      )
                    );
                  },
                }}
              />
            </div>
          </div>
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_MAS_SET_REG_007;
